/**
 * SCRUM-236 (DS-015 §13.8, D6) — English message catalog for the entity-change
 * / Activity surface. Backend stores stable structured descriptors + i18n keys
 * (D6); this catalog is the FE rendering of those keys.
 *
 * This is the FIRST i18n catalog in Sparta (react-intl was installed but unused
 * before this ticket). It is intentionally scoped to entity-change rendering;
 * other surfaces migrate to react-intl on their own tickets. The i18n-guardian
 * ESLint rule (bare-string detection) is NOT introduced here — per CLAUDE.md it
 * flips on when the first non-English locale ships, not when react-intl is
 * bootstrapped.
 *
 * Field-label and enum-value ids are built with the {@link fieldLabelId} /
 * {@link enumValueId} helpers so they match exactly what the registry and
 * formatters look up at runtime (a missing entry surfaces via the IntlProvider
 * `onError` dev warning + the registry coverage drift test).
 */
import { HISTORY_MESSAGE_IDS as H, enumValueId, fieldLabelId, semanticLabelId } from '../message-ids.js';

const PROPERTY_ENTITY = 'Property';
const GUEST_ENTITY = 'Guest';
const PAYMENT_TYPE_ENTITY = 'PaymentType';
const FOLIO_ENTITY = 'Folio';
const PAYMENT_SCHEDULE_TEMPLATE_ENTITY = 'PaymentScheduleTemplate';
const PAYMENT_ENTITY = 'Payment';
const RESERVATION_ENTITY = 'Reservation';

/** Human labels for every tracked Property field (keyed by the DB-field / wire key). */
const PROPERTY_FIELD_LABELS: Readonly<Record<string, string>> = {
  unitName: 'Unit name',
  unitAlias: 'Unit alias',
  websiteNameOverride: 'Website name override',
  unitCategory: 'Unit category',
  listingType: 'Listing type',
  statusBilling: 'Billing status',
  statusVisibility: 'Visibility',
  descriptionCanonical: 'Description',
  clonedFromPropertyId: 'Cloned from property',
  clonedFromOrganizationId: 'Cloned from organization',
  parentPropertyId: 'Parent unit',
  photoCount: 'Photo count',
  archivedAt: 'Archived at',
  weekdayBasePriceCents: 'Weekday base price',
  weekendBasePriceCents: 'Weekend base price',
  weekdayFloorRateCents: 'Weekday floor rate',
  weekendFloorRateCents: 'Weekend floor rate',
  licenseNumber: 'License number',
  vatNumber: 'VAT number',
  houseRules: 'House rules',
  aboutThisSpace: 'About this space',
  additionalRules: 'Additional rules',
  localLaws: 'Local laws',
  streetLine1: 'Street line 1',
  streetLine2: 'Street line 2',
  city: 'City',
  state: 'State / region',
  postalCode: 'Postal code',
  country: 'Country',
  latitude: 'Latitude',
  longitude: 'Longitude',
  timezone: 'Time zone',
  maxSleeps: 'Max sleeps',
  maxAdults: 'Max adults',
  standardCapacity: 'Standard capacity',
  allowsChildren: 'Allows children',
  allowsPets: 'Allows pets',
  bedrooms: 'Bedrooms',
  bathroomsFull: 'Full bathrooms',
  bathroomsHalf: 'Half bathrooms',
  bathroomsShower: 'Shower bathrooms',
  bathroomsBathtub: 'Bathtub bathrooms',
  bedConfigurations: 'Bed configurations',
  checkInOverride: 'Check-in time',
  checkOutOverride: 'Check-out time',
};

/** Enum-value display labels per enum field (values mirror @artemis/contracts). */
const PROPERTY_ENUM_LABELS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  unitCategory: {
    home: 'Home',
    condominium: 'Condominium',
    villa: 'Villa',
    cabin: 'Cabin',
    townhome: 'Townhome',
    apartment: 'Apartment',
    other: 'Other',
  },
  listingType: {
    entire_unit: 'Entire unit',
    private_room: 'Private room',
    shared_room: 'Shared room',
    other: 'Other',
  },
  statusBilling: {
    active: 'Active',
    inactive: 'Inactive',
  },
  statusVisibility: {
    bookable: 'Bookable',
    non_bookable: 'Not bookable',
  },
};

/** Human labels for every tracked Guest field (keyed by the DB-field / wire key). SCRUM-257. */
const GUEST_FIELD_LABELS: Readonly<Record<string, string>> = {
  givenName: 'Given name',
  familyName: 'Family name',
  displayName: 'Display name',
  primaryEmail: 'Primary email',
  primaryPhone: 'Primary phone',
  addressLine1: 'Address line 1',
  addressLine2: 'Address line 2',
  addressCity: 'City',
  addressStateProvince: 'State / province',
  addressPostalCode: 'Postal code',
  addressCountry: 'Country',
  tags: 'Tags',
  notes: 'Notes',
  source: 'Source',
  status: 'Status',
  mergedIntoId: 'Merged into',
  deletedAt: 'Archived at',
};

/** Guest enum-value display labels per enum field (values mirror the backend domain unions). */
const GUEST_ENUM_LABELS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  status: {
    Active: 'Active',
    Archived: 'Archived',
    MergedAway: 'Merged away',
  },
  source: {
    Manual: 'Manual',
  },
};

/** Human labels for every tracked PaymentType field (keyed by the DB-field / wire key). SCRUM-290. */
const PAYMENT_TYPE_FIELD_LABELS: Readonly<Record<string, string>> = {
  paymentName: 'Payment name',
  baseSystemType: 'Base system type',
  requiresRefund: 'Requires refund',
  processor: 'Processor',
  status: 'Status',
  systemKey: 'System key',
};

/** PaymentType enum-value display labels per enum field. */
const PAYMENT_TYPE_ENUM_LABELS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  // BaseSystemType — 4 custom-mapping targets.
  baseSystemType: {
    Cash: 'Cash',
    Owner: 'Owner',
    Adjustment: 'Adjustment',
    Channel: 'Channel',
  },
  // PaymentTypeStatus.
  status: {
    Active: 'Active',
    Inactive: 'Inactive',
  },
  // SystemPaymentTypeKey — 6 blueprint system keys.
  systemKey: {
    CC: 'Credit / Debit card',
    ACH: 'ACH / Bank transfer',
    Cash: 'Cash',
    Adjustment: 'Adjustment',
    Owner: 'Owner charge',
    Channel: 'Channel collect',
  },
};

/** Human labels for every tracked Folio field (keyed by the DB-field / wire key). SCRUM-291. */
const FOLIO_FIELD_LABELS: Readonly<Record<string, string>> = {
  reservationId: 'Reservation',
  currency: 'Currency',
  collectionModel: 'Collection model',
  totalDueCents: 'Total due',
  netCollectedCents: 'Net collected',
  status: 'Status',
  // CXL-1 (SCRUM-288 review): the staff funds-resolution decision at cancellation.
  cancellationPathway: 'Cancellation funds pathway',
};

/** Folio enum-value display labels per enum field (values mirror the backend domain unions). */
const FOLIO_ENUM_LABELS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  // CollectionModel.
  collectionModel: {
    ChannelCollect: 'Channel collect',
    PropertyCollect: 'Property collect',
  },
  // FolioStatus.
  status: {
    Open: 'Open',
    Closed: 'Closed',
    Locked: 'Locked',
  },
  // CancellationPathway (DS-021 §8) — the three funds-resolution routings.
  cancellationPathway: {
    FutureStayCredit: 'Future stay credit',
    Disburse: 'Disburse to owner/company/fees',
    CancellationFee: 'Cancellation fee',
  },
};

/** Human labels for every tracked PaymentScheduleTemplate field (keyed by the DB-field / wire key). SCRUM-291. */
const PAYMENT_SCHEDULE_TEMPLATE_FIELD_LABELS: Readonly<Record<string, string>> = {
  scope: 'Scope',
  propertyId: 'Property',
  breakdownKind: 'Breakdown kind',
  params: 'Parameters',
  effectiveFrom: 'Effective from',
  status: 'Status',
};

/** PaymentScheduleTemplate enum-value display labels per enum field. */
const PAYMENT_SCHEDULE_TEMPLATE_ENUM_LABELS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  // PaymentScheduleTemplateScope (ADR 0026: no unit concept; Property IS the sellable unit).
  scope: {
    'org-global': 'Organization-wide',
    property: 'Property',
  },
  // BreakdownKind.
  breakdownKind: {
    PaidInFull: 'Paid in full',
    TwoStep: 'Two-step',
    MultiPayment: 'Multi-payment',
  },
  // PaymentScheduleTemplateStatus.
  status: {
    Active: 'Active',
    Inactive: 'Inactive',
  },
};

/** Human labels for every tracked Payment field (keyed by the DB-field / wire key). SCRUM-292. */
const PAYMENT_FIELD_LABELS: Readonly<Record<string, string>> = {
  state: 'State',
  baseSystemType: 'Base system type',
  processingRail: 'Processing rail',
  amountCents: 'Amount',
  kind: 'Kind',
  isCashMovement: 'Cash movement',
  requiresRefund: 'Requires refund',
  paymentTypeLabel: 'Payment type',
  gatewayRef: 'Gateway reference',
  processorName: 'Processor',
  occurredAt: 'Occurred at',
};

/** Payment enum-value display labels per enum field (mirror the backend domain unions). */
const PAYMENT_ENUM_LABELS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  // PaymentState FSM states.
  state: {
    Intended: 'Intended',
    Authorized: 'Authorized',
    Captured: 'Captured',
    Settled: 'Settled',
    Voided: 'Voided',
    Failed: 'Failed',
    Refunded: 'Refunded',
    PartiallyRefunded: 'Partially refunded',
  },
  // BaseSystemType — all 6 values.
  baseSystemType: {
    CC: 'Credit / Debit card',
    ACH: 'ACH / Bank transfer',
    Cash: 'Cash',
    Adjustment: 'Adjustment',
    Owner: 'Owner charge',
    Channel: 'Channel collect',
  },
  // ProcessingRail.
  processingRail: {
    manual: 'Manual',
    gateway: 'Gateway',
  },
  // PaymentKind.
  kind: {
    charge: 'Charge',
    refund: 'Refund',
  },
};

/** Human labels for every tracked Reservation field (keyed by the DB-field / wire key). SCRUM-338. */
const RESERVATION_FIELD_LABELS: Readonly<Record<string, string>> = {
  status: 'Status',
  stayKind: 'Stay kind',
  heardAbout: 'Heard about',
  cancellationPathway: 'Cancellation pathway',
  kind: 'Occupancy kind',
  reservationTypeKey: 'Reservation type',
  channelReservationId: 'Channel reservation ID',
  guestDisplayName: 'Guest name',
  arrivalDate: 'Arrival date',
  departureDate: 'Departure date',
  processor: 'Channel processor',
  guestId: 'Guest',
  propertyId: 'Property',
  guestCount: 'Guest count',
  ratesLocked: 'Rates locked',
  cancelledAt: 'Cancelled at',
  confirmedAt: 'Confirmed at',
  holdExpiresAt: 'Hold expires at',
};

/** Reservation enum-value display labels per enum field (mirror the booking domain unions). */
const RESERVATION_ENUM_LABELS: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  // ReservationStatus lifecycle states.
  status: {
    QUO: 'Quote',
    HOLD: 'Hold',
    BKD: 'Booked',
    CI: 'Checked in',
    CO: 'Checked out',
    CXL: 'Cancelled',
    HIST: 'Historical',
  },
  // StayKind.
  stayKind: {
    PayingGuest: 'Paying guest',
    OwnerStay: 'Owner stay',
    NonStayBlock: 'Non-stay block',
  },
  // HeardAbout provenance (§2.9).
  heardAbout: {
    Social: 'Social',
    Email: 'Email',
    OTA: 'OTA',
    Direct: 'Direct',
    GEO: 'GEO',
    Search: 'Search',
  },
  // CancellationPathway — booking BC wire values.
  cancellationPathway: {
    'Hold-credit': 'Hold credit',
    Disburse: 'Disburse',
    'Cancellation-fee': 'Cancellation fee',
  },
  // Occupancy discriminant.
  kind: {
    stay: 'Stay',
    block: 'Block',
  },
};

/**
 * Backend `semanticLabelKey` values (DS-015 §3) used verbatim as message ids.
 * Today Property emits `property.status_changed` and `property.archived`.
 */
const PROPERTY_SEMANTIC_LABELS: Readonly<Record<string, string>> = {
  'property.status_changed': 'Status changed',
  'property.archived': 'Archived',
};

/**
 * Reservation semantic-label overlays (SCRUM-338 A.1). Keyed by verb; the
 * message id is built with {@link semanticLabelId} so it matches the backend
 * capture spec's literal key `entityChange.semantic.Reservation.<verb>` EXACTLY.
 * These are the human sentences the timeline shows above the field deltas.
 */
const RESERVATION_SEMANTIC_LABELS: Readonly<Record<string, string>> = {
  quoted: 'Quote created',
  held: 'Hold placed',
  confirmed: 'Confirmed',
  modified: 'Modified',
  checked_in: 'Checked in',
  checked_out: 'Checked out',
  cancelled: 'Cancelled',
  historic_imported: 'Imported',
};

function buildEnMessages(): Record<string, string> {
  const messages: Record<string, string> = {
    // Fixed change-rendering vocabulary.
    [H.valueUnknown]: '—',
    [H.valueComplex]: 'updated',
    [H.valueCleared]: 'cleared',
    [H.booleanTrue]: 'Yes',
    [H.booleanFalse]: 'No',
    [H.changeConnector]: '{from} → {to}',
    [H.changeRedacted]: 'changed',
    [H.semanticCreated]: 'Created',
    [H.semanticUpdated]: 'Updated',
    [H.semanticDeleted]: 'Deleted',
    // Backend semantic-label keys (used as ids verbatim).
    ...PROPERTY_SEMANTIC_LABELS,
  };
  // Reservation semantic overlays — keyed by verb, id built via semanticLabelId
  // so it matches the backend capture spec's literal key exactly (SCRUM-338 A.1).
  for (const [verb, label] of Object.entries(RESERVATION_SEMANTIC_LABELS)) {
    messages[semanticLabelId(RESERVATION_ENTITY, verb)] = label;
  }
  addFieldLabels(messages, PROPERTY_ENTITY, PROPERTY_FIELD_LABELS);
  addEnumLabels(messages, PROPERTY_ENTITY, PROPERTY_ENUM_LABELS);
  addFieldLabels(messages, GUEST_ENTITY, GUEST_FIELD_LABELS);
  addEnumLabels(messages, GUEST_ENTITY, GUEST_ENUM_LABELS);
  addFieldLabels(messages, PAYMENT_TYPE_ENTITY, PAYMENT_TYPE_FIELD_LABELS);
  addEnumLabels(messages, PAYMENT_TYPE_ENTITY, PAYMENT_TYPE_ENUM_LABELS);
  addFieldLabels(messages, FOLIO_ENTITY, FOLIO_FIELD_LABELS);
  addEnumLabels(messages, FOLIO_ENTITY, FOLIO_ENUM_LABELS);
  addFieldLabels(messages, PAYMENT_SCHEDULE_TEMPLATE_ENTITY, PAYMENT_SCHEDULE_TEMPLATE_FIELD_LABELS);
  addEnumLabels(messages, PAYMENT_SCHEDULE_TEMPLATE_ENTITY, PAYMENT_SCHEDULE_TEMPLATE_ENUM_LABELS);
  addFieldLabels(messages, PAYMENT_ENTITY, PAYMENT_FIELD_LABELS);
  addEnumLabels(messages, PAYMENT_ENTITY, PAYMENT_ENUM_LABELS);
  addFieldLabels(messages, RESERVATION_ENTITY, RESERVATION_FIELD_LABELS);
  addEnumLabels(messages, RESERVATION_ENTITY, RESERVATION_ENUM_LABELS);
  return messages;
}

function addFieldLabels(
  messages: Record<string, string>,
  entity: string,
  labels: Readonly<Record<string, string>>,
): void {
  for (const [field, label] of Object.entries(labels)) {
    messages[fieldLabelId(entity, field)] = label;
  }
}

function addEnumLabels(
  messages: Record<string, string>,
  entity: string,
  enumLabels: Readonly<Record<string, Readonly<Record<string, string>>>>,
): void {
  for (const [field, values] of Object.entries(enumLabels)) {
    for (const [value, label] of Object.entries(values)) {
      messages[enumValueId(entity, field, value)] = label;
    }
  }
}

/** The compiled English catalog consumed by the IntlProvider. */
export const EN_MESSAGES: Readonly<Record<string, string>> = buildEnMessages();

/** Default + only locale today. */
export const DEFAULT_LOCALE = 'en';
